"use client";

import Image from "next/image";
import { ArrowRight, MapPin, Wrench } from "lucide-react";
import { useTranslations } from "next-intl";
import type { BlogPost } from "@/components/blog/types";
import { Link } from "@/i18n/navigation";
import { urlFor } from "@/lib/image";

type FaqResourcesSectionProps = {
  featuredBlogPosts: BlogPost[];
  featuredNewsPosts: BlogPost[];
};

type FeaturedListProps = {
  title: string;
  description: string;
  posts: BlogPost[];
  emptyLabel: string;
  ctaLabel: string;
  href: string;
};

const getPostImage = (post: BlogPost) => {
  const source = post.ogImage ?? post.mainImage ?? post.coverImage;
  if (!source) return "/og-image.png";

  try {
    return urlFor(source).width(760).height(420).url();
  } catch {
    return "/og-image.png";
  }
};

const getPostExcerpt = (post: BlogPost) => {
  const text = post.seoDescription || post.excerpt || "";
  if (text.length <= 150) return text;
  return `${text.slice(0, 150).trim()}...`;
};

const FeaturedList = ({ title, description, posts, emptyLabel, ctaLabel, href }: FeaturedListProps) => (
  <article className="surface-card border border-gray-200 p-6 dark:border-gray-700">
    <h3 className="text-2xl font-bold text-brand-navy dark:text-white">{title}</h3>
    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{description}</p>

    {posts.length > 0 ? (
      <div className="mt-5 space-y-4">
        {posts.slice(0, 3).map((post) => (
          <Link
            key={post.slug}
            href={`/${post.slug}`}
            className="group block overflow-hidden rounded-2xl border border-gray-200 bg-white transition hover:-translate-y-0.5 hover:border-brand-red dark:border-gray-700 dark:bg-surface-dark-raised"
          >
            <div className="relative h-40 w-full">
              <Image
                src={getPostImage(post)}
                alt={post.ogImage?.alt || post.title}
                fill
                className="object-cover transition-transform duration-300 group-hover:scale-105"
                sizes="(max-width: 1024px) 100vw, 340px"
              />
            </div>
            <div className="p-4">
              <p className="text-base font-semibold leading-snug text-gray-900 dark:text-gray-100">
                {post.seoTitle || post.title}
              </p>
              {getPostExcerpt(post) && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{getPostExcerpt(post)}</p>
              )}
            </div>
          </Link>
        ))}
      </div>
    ) : (
      <p className="mt-5 rounded-2xl border border-dashed border-gray-300 p-4 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-300">
        {emptyLabel}
      </p>
    )}

    <div className="mt-6">
      <Link
        href={href}
        className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-navy-dark"
      >
        {ctaLabel}
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </article>
);

export default function FaqResourcesSection({
  featuredBlogPosts,
  featuredNewsPosts,
}: FaqResourcesSectionProps) {
  const t = useTranslations("faqPage.resources");

  return (
    <section className="bg-surface-light px-4 section-y dark:bg-surface-dark">
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="text-center">
          <h2 className="text-brand-navy dark:text-white type-h2">{t("title")}</h2>
          <p className="mx-auto mt-3 max-w-3xl text-gray-700 dark:text-gray-300">{t("subtitle")}</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <FeaturedList
            title={t("news.title")}
            description={t("news.description")}
            posts={featuredNewsPosts}
            emptyLabel={t("news.empty")}
            ctaLabel={t("news.cta")}
            href="/noticias"
          />
          <FeaturedList
            title={t("blog.title")}
            description={t("blog.description")}
            posts={featuredBlogPosts}
            emptyLabel={t("blog.empty")}
            ctaLabel={t("blog.cta")}
            href="/blog"
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <article className="surface-card border border-gray-200 p-6 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-navy/10 text-brand-navy dark:bg-white/10 dark:text-white">
                <Wrench className="h-5 w-5" />
              </span>
              <h3 className="text-xl font-bold text-brand-navy dark:text-white">{t("maintenance.title")}</h3>
            </div>
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">{t("maintenance.description")}</p>
            <div className="mt-6">
              <Link
                href="/mantenimiento-instalaciones-fotovoltaicas"
                className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-navy-dark"
              >
                {t("maintenance.cta")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </article>

          <article className="surface-card border border-gray-200 p-6 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-navy/10 text-brand-navy dark:bg-white/10 dark:text-white">
                <MapPin className="h-5 w-5" />
              </span>
              <h3 className="text-xl font-bold text-brand-navy dark:text-white">{t("coverage.title")}</h3>
            </div>
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">{t("coverage.description")}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                href="/placas-solares-sevilla"
                className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-brand-navy transition hover:bg-gray-200 dark:bg-[#1f2937] dark:text-gray-100"
              >
                {t("coverage.sevilla")}
              </Link>
              <Link
                href="/placas-solares-madrid"
                className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-brand-navy transition hover:bg-gray-200 dark:bg-[#1f2937] dark:text-gray-100"
              >
                {t("coverage.madrid")}
              </Link>
              <Link
                href="/placas-solares-huelva"
                className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-brand-navy transition hover:bg-gray-200 dark:bg-[#1f2937] dark:text-gray-100"
              >
                {t("coverage.huelva")}
              </Link>
              <Link
                href="/placas-solares-cadiz"
                className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-brand-navy transition hover:bg-gray-200 dark:bg-[#1f2937] dark:text-gray-100"
              >
                {t("coverage.cadiz")}
              </Link>
              <Link
                href="/subvenciones-placas-solares-andalucia"
                className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-brand-navy transition hover:bg-gray-200 dark:bg-[#1f2937] dark:text-gray-100"
              >
                {t("coverage.andalucia")}
              </Link>
            </div>
            <div className="mt-6">
              <Link
                href="/contacto"
                className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-navy-dark"
              >
                {t("coverage.cta")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
